import React, { useContext, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { Edit, Trash2, Plus, Search, ChevronLeft, ChevronRight, Mail, Users, ArrowUp, ArrowDown } from 'lucide-react';
import { useUserList } from './shared/services/useUserService';

const ROWS_PER_PAGE = 5;

const UserListPage = () => {
  const { userRole, permissions } = useContext(AuthContext);
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '0', 10);
  const { users, loading, deleteUser } = useUserList();
  const [sortConfig, setSortConfig] = useState({ key: 'fullname', direction: 'asc' });

  const hasPermission = (perm) => (userRole || '').toLowerCase() === 'admin' || (permissions || []).includes(perm);
  const showActionsColumn = hasPermission('Users Edit') || hasPermission('Users Delete');

  const filtered = users.filter(u =>
    (u.fullname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Apply Sorting
  filtered.sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aVal = (a[sortConfig.key] || '').toString().toLowerCase();
    const bVal = (b[sortConfig.key] || '').toString().toLowerCase();
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const paginated = filtered.slice(currentPage * ROWS_PER_PAGE, (currentPage + 1) * ROWS_PER_PAGE);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };
  const resetSearch = () => { setSearchTerm(''); setSearchParams({ page: '0' }); };

  const handleDelete = (user) => {
    showNotification('confirm', `Delete user ${user.fullname}?`, async () => {
      try { await deleteUser(user); showNotification('success', 'User deleted successfully'); }
      catch (err) { showNotification('error', err.response?.data?.message || 'Error deleting user'); }
    });
  };

  if (loading) return (
    <div className="w-full max-w-6xl mx-auto p-4 animate-pulse space-y-3">
      <div className="h-8 bg-gray-200 rounded mb-4"></div>
      {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-gray-200 rounded"></div>)}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 space-y-6">
      
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 h-5 w-5" />
            <input 
              type="text" 
              placeholder="Search users by name or email..." 
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setSearchParams({ page: '0' }); }}
              className="w-full pl-12 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all" 
            />
            {searchTerm && (
              <button 
                onClick={resetSearch} 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-200 rounded-full transition-colors"
              >
                ✕
              </button>
            )}
          </div>

          {hasPermission('Users Add') && (
            <button 
              onClick={() => navigate('/addUser')} 
              className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-6 py-2.5 rounded-lg font-bold transition-all shadow-sm active:scale-95 whitespace-nowrap justify-center"
            >
              <Plus className="h-5 w-5" /> Add User
            </button>
          )}
        </div>
      </div>

      {searchTerm && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-800">Found {filtered.length} user{filtered.length !== 1 ? 's' : ''} matching "{searchTerm}"</div>}

      {/* Desktop Table */}
      <div className="hidden lg:block bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
              {[
                { label: 'S/No', key: null },
                { label: 'User Name', key: 'fullname' },
                { label: 'Email', key: 'email' },
                { label: 'Last Login', key: 'lastlogin' },
                showActionsColumn && { label: 'Actions', key: null }
              ].filter(Boolean).map(h => (
                <th 
                  key={h.label} 
                  onClick={() => h.key && requestSort(h.key)}
                  className={`px-4 py-4 font-bold text-slate-700 text-sm tracking-tight ${h.key ? 'cursor-pointer select-none hover:bg-slate-100/50' : ''} transition-colors`}
                >
                  <div className="flex items-center gap-1.5">
                    {h.label}
                    {sortConfig.key === h.key && (
                      <div className="text-teal-600">
                        {sortConfig.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr><td colSpan={showActionsColumn ? 5 : 4} className="p-12 text-center text-slate-500">No users found</td></tr>
            ) : paginated.map((user, i) => (
              <tr key={user.user_id} className={`border-b border-slate-100 hover:bg-teal-50/50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                <td className="px-4 py-4 font-medium text-slate-900 text-sm whitespace-nowrap">{currentPage * ROWS_PER_PAGE + i + 1}</td>
                <td className="px-4 py-4 font-bold text-slate-900">{user.fullname}</td>
                <td className="px-4 py-4"><div className="flex items-center gap-2 text-slate-600"><Mail className="h-4 w-4 text-slate-400" />{user.email}</div></td>
                <td className="px-4 py-4 text-xs text-slate-500 italic">{user.lastlogin || 'Never'}</td>
                {showActionsColumn && (
                  <td className="px-4 py-4">
                    <div className="flex gap-2">
                      {hasPermission('Users Edit') && (
                        <button 
                          onClick={() => navigate(`/editUser/${user.user_id}`, { 
                            state: { activeMenu: 'User List', page: currentPage } 
                          })} 
                          className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors font-medium"
                        >
                          <Edit className="h-4 w-4" />
                          Edit
                        </button>
                      )}
                      {hasPermission('Users Delete') && (
                        <button 
                          onClick={() => handleDelete(user)} 
                          className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors font-medium"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {paginated.map((user, i) => (
          <div key={user.user_id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <span className="bg-teal-100 text-teal-800 px-2 py-1 rounded text-sm font-medium">#{currentPage * ROWS_PER_PAGE + i + 1}</span>
            <h3 className="font-semibold text-gray-900 text-lg mt-2 mb-1">{user.fullname}</h3>
            <div className="flex items-center gap-3 mb-4 text-gray-600"><Mail className="h-4 w-4" />{user.email}</div>
            <div className="flex gap-2 pt-3 border-t border-gray-100 flex-wrap">
              {hasPermission('Users Edit') && (
                <button 
                  onClick={() => navigate(`/editUser/${user.user_id}`, { 
                    state: { activeMenu: 'User List', page: currentPage } 
                  })} 
                  className="flex items-center justify-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg text-sm font-medium transition-colors"
                >
                  <Edit className="h-4 w-4" />
                  Edit
                </button>
              )}
              {hasPermission('Users Delete') && <button onClick={() => handleDelete(user)} className="flex-1 flex items-center justify-center gap-2 py-2 text-red-600 hover:bg-red-50 border border-red-200 rounded-lg text-sm"><Trash2 className="h-4 w-4" />Delete</button>}
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {filtered.length > 0 && totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600">Showing {currentPage * ROWS_PER_PAGE + 1}–{Math.min((currentPage + 1) * ROWS_PER_PAGE, filtered.length)} of {filtered.length} users</div>
            <div className="flex items-center gap-2">
              <button onClick={() => setSearchParams({ page: '0' })} disabled={currentPage === 0} className="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">First</button>
              <button onClick={() => setSearchParams({ page: (currentPage - 1).toString() })} disabled={currentPage === 0} className="p-2 rounded-lg border border-gray-200 disabled:opacity-50"><ChevronLeft className="h-4 w-4" /></button>
              <span className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg">{currentPage + 1} of {totalPages}</span>
              <button onClick={() => setSearchParams({ page: (currentPage + 1).toString() })} disabled={currentPage >= totalPages - 1} className="p-2 rounded-lg border border-gray-200 disabled:opacity-50"><ChevronRight className="h-4 w-4" /></button>
              <button onClick={() => setSearchParams({ page: (totalPages - 1).toString() })} disabled={currentPage >= totalPages - 1} className="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">Last</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserListPage;
